import type { WorldTemplate } from '@creanizer/types'

export interface WorldTemplateData {
  id: WorldTemplate
  name: string
  description: string
  defaultParameters?: {
    magicLevel?: number
    scienceLevel?: number
    civilizationLevel?: number
    dangerLevel?: number
  }
}

export interface TemplateCategory {
  id: string
  name: string
  description: string
  templates: WorldTemplateData[]
}

// ジャンル特化テンプレート
const GENRE_TEMPLATES: WorldTemplateData[] = [
  {
    id: 'modern-ability-battle' as WorldTemplate,
    name: '現代異能バトル',
    description: '現代社会の裏で繰り広げられる超能力者たちの戦い。学園、都市、秘密組織が舞台',
    defaultParameters: {
      magicLevel: 6,  // 異能力はあるが魔法ではない
      scienceLevel: 8, // 現代〜近未来
      civilizationLevel: 8,
      dangerLevel: 7,
    },
  },
  {
    id: 'japanese-historical-fantasy' as WorldTemplate,
    name: '和風歴史伝奇',
    description: '平安〜江戸時代の日本。忍者、陰陽師、侍、妖怪退治。和の美と怪異',
    defaultParameters: {
      magicLevel: 7,  // 妖術・忍術・陰陽術
      scienceLevel: 2, // 前近代
      civilizationLevel: 5,
      dangerLevel: 7,
    },
  },
  {
    id: 'meiji-taisho-romance' as WorldTemplate,
    name: '明治大正剣客浪漫',
    description: '文明開化の時代。剣客、新選組の生き残り、西洋文明との融合、浪漫と哀愁',
    defaultParameters: {
      magicLevel: 3,  // 超人的剣技程度
      scienceLevel: 5, // 近代化途上
      civilizationLevel: 7,
      dangerLevel: 6,
    },
  },
  {
    id: 'modern-folklore-battle' as WorldTemplate,
    name: '現代伝奇バトル',
    description: '妖怪・神話・伝説が現代に蘇る。陰陽師、退魔師、都市と神話の融合',
    defaultParameters: {
      magicLevel: 8,  // 妖術・神通力
      scienceLevel: 7, // 現代
      civilizationLevel: 8,
      dangerLevel: 8,
    },
  },
  {
    id: 'classic-light-fantasy' as WorldTemplate,
    name: '王道ライトファンタジー',
    description: '剣と魔法の冒険活劇。勇者、魔王、仲間との旅、明るく希望に満ちた世界',
    defaultParameters: {
      magicLevel: 7,  // 魔法は特別な力
      scienceLevel: 3, // 中世レベル
      civilizationLevel: 5,
      dangerLevel: 5,  // 適度な冒険
    },
  },
  {
    id: 'dungeon-crawler-hardcore' as WorldTemplate,
    name: '迷宮探索ハードコアファンタジー',
    description: '死と隣り合わせの地下迷宮。蘇生困難、罠と魔物、一歩間違えば全滅',
    defaultParameters: {
      magicLevel: 6,  // 魔法は強力だが制限あり
      scienceLevel: 3, // 中世レベル
      civilizationLevel: 4,
      dangerLevel: 10,  // 極めて高い死亡率
    },
  },
  {
    id: 'medieval-fantasy-war' as WorldTemplate,
    name: '中世ファンタジー戦記',
    description: '国家間戦争、騎士団、軍略と政治。重厚な中世世界での戦いと謀略',
    defaultParameters: {
      magicLevel: 5,  // 魔法は限定的
      scienceLevel: 3, // 中世レベル
      civilizationLevel: 6,
      dangerLevel: 8,  // 戦争の脅威
    },
  },
  {
    id: 'narou-fantasy' as WorldTemplate,
    name: 'なろうファンタジー',
    description: '転生・転移先の中世風ファンタジー世界。スキル、ステータス、冒険者ギルド',
    defaultParameters: {
      magicLevel: 9,  // 魔法が一般的
      scienceLevel: 3, // 中世レベル
      civilizationLevel: 5,
      dangerLevel: 6,
    },
  },
  {
    id: 'real-robot-sf' as WorldTemplate,
    name: 'リアル系ロボットバトルSF',
    description: '軍事・政治色の強いロボット戦争。宇宙コロニー、地球連邦、独立戦争',
    defaultParameters: {
      magicLevel: 1,  // 魔法なし
      scienceLevel: 9, // 近未来〜未来
      civilizationLevel: 9,
      dangerLevel: 9,
    },
  },
  {
    id: 'super-robot-anime' as WorldTemplate,
    name: 'スーパーロボット系アニメ',
    description: '正義と悪の戦い、必殺技、合体ロボット。熱血と友情が世界を救う',
    defaultParameters: {
      magicLevel: 3,  // 謎エネルギー
      scienceLevel: 8, // 超科学
      civilizationLevel: 7,
      dangerLevel: 8,
    },
  },
  {
    id: 'rural-horror' as WorldTemplate,
    name: '因習村ホラー',
    description: '閉鎖的な村社会、古い因習、禁忌の儀式。外部から来た者を襲う恐怖',
    defaultParameters: {
      magicLevel: 5,  // 呪術・祟り
      scienceLevel: 6, // 現代だが遅れた地域
      civilizationLevel: 4,
      dangerLevel: 9,
    },
  },
  {
    id: 'urban-legend-horror' as WorldTemplate,
    name: '現代都市伝説怪異譚',
    description: '都市伝説、学校の怪談、ネット怪談。日常に潜む都市型の恐怖',
    defaultParameters: {
      magicLevel: 4,  // 怪異・呪い
      scienceLevel: 8, // 現代
      civilizationLevel: 8,
      dangerLevel: 7,
    },
  },
  {
    id: 'anomaly-containment' as WorldTemplate,
    name: '異常存在管理機構',
    description: '秘密組織が異常存在を収容・研究。科学と超常の境界、世界の裏側を守る',
    defaultParameters: {
      magicLevel: 6,  // 異常現象として存在
      scienceLevel: 9, // 最先端技術で対抗
      civilizationLevel: 9,
      dangerLevel: 9,  // 世界規模の脅威
    },
  },
  {
    id: 'idol-entertainment' as WorldTemplate,
    name: 'アイドルエンタメ',
    description: '芸能界、アイドル活動、ライブステージ。夢と努力、仲間との絆',
    defaultParameters: {
      magicLevel: 2,  // 奇跡的な演出程度
      scienceLevel: 8, // 現代〜近未来
      civilizationLevel: 9,
      dangerLevel: 3,  // 競争やスキャンダル
    },
  },
  {
    id: 'light-magical-girl' as WorldTemplate,
    name: '王道魔法少女',
    description: '愛と正義、友情の力で戦う魔法少女。明るく希望に満ちた世界',
    defaultParameters: {
      magicLevel: 9,  // 魔法が中心
      scienceLevel: 7, // 現代
      civilizationLevel: 8,
      dangerLevel: 5,  // 適度な脅威
    },
  },
  {
    id: 'dark-magical-girl' as WorldTemplate,
    name: 'ダーク魔法少女',
    description: '犠牲と代償を伴う魔法少女。残酷な運命、シリアスな心理描写',
    defaultParameters: {
      magicLevel: 8,  // 魔法には代償がある
      scienceLevel: 7, // 現代
      civilizationLevel: 8,
      dangerLevel: 9,  // 高い死亡率と絶望
    },
  },
  {
    id: 'transform-hero' as WorldTemplate,
    name: '変身ヒーロー',
    description: '変身能力を持つ個人ヒーロー。街を守る正義の味方、怪人との戦い',
    defaultParameters: {
      magicLevel: 4,  // 変身能力
      scienceLevel: 8, // 現代〜近未来
      civilizationLevel: 8,
      dangerLevel: 7,  // 怪人の脅威
    },
  },
  {
    id: 'sentai-hero' as WorldTemplate,
    name: '戦隊ヒーロー',
    description: 'チームで戦うカラフルヒーロー。巨大ロボ、悪の組織、友情と団結',
    defaultParameters: {
      magicLevel: 3,  // 不思議パワー
      scienceLevel: 8, // 超科学
      civilizationLevel: 8,
      dangerLevel: 7,  // 組織的な脅威
    },
  },
  {
    id: 'modern-detective-mystery' as WorldTemplate,
    name: '現代名探偵ミステリ',
    description: '現代社会の謎と事件。名探偵、トリック、論理的推理、密室殺人',
    defaultParameters: {
      magicLevel: 1,  // 魔法なし、純粋な推理
      scienceLevel: 8, // 現代技術
      civilizationLevel: 9,
      dangerLevel: 5,  // 殺人事件などの犯罪
    },
  },
  {
    id: 'sekai-kei' as WorldTemplate,
    name: 'セカイ系',
    description: '主人公の選択が世界の運命を決める。内面と世界の直結、終末的状況',
    defaultParameters: {
      magicLevel: 6,  // 謎の力や特殊能力
      scienceLevel: 8, // 現代〜近未来
      civilizationLevel: 8,
      dangerLevel: 10,  // 世界の存亡
    },
  },
]

// 特殊な世界構造テンプレート
const SPECIAL_STRUCTURE_TEMPLATES: WorldTemplateData[] = [
  {
    id: 'floating-islands',
    name: '浮遊島世界',
    description: '空に浮かぶ島々が舞台の世界',
    defaultParameters: {
      magicLevel: 8,
      scienceLevel: 3,
      dangerLevel: 6,
    },
  },
  {
    id: 'underground-civilization',
    name: '地下文明',
    description: '地底に築かれた文明世界',
    defaultParameters: {
      magicLevel: 4,
      scienceLevel: 6,
      dangerLevel: 7,
    },
  },
  {
    id: 'ocean-world',
    name: '海洋世界',
    description: '大部分が海に覆われた世界',
    defaultParameters: {
      magicLevel: 5,
      scienceLevel: 5,
      dangerLevel: 6,
    },
  },
  {
    id: 'desert-planet',
    name: '砂漠惑星',
    description: '砂漠が支配する過酷な惑星',
    defaultParameters: {
      magicLevel: 3,
      scienceLevel: 7,
      dangerLevel: 8,
    },
  },
  {
    id: 'forest-realm',
    name: '森林世界',
    description: '巨大な森に覆われた世界',
    defaultParameters: {
      magicLevel: 7,
      scienceLevel: 2,
      dangerLevel: 5,
    },
  },
  {
    id: 'city-planet',
    name: '都市惑星',
    description: '惑星全体が都市化された世界',
    defaultParameters: {
      magicLevel: 1,
      scienceLevel: 10,
      civilizationLevel: 9,
    },
  },
  {
    id: 'ring-world',
    name: 'リングワールド',
    description: '恒星を囲む巨大な環状構造物',
    defaultParameters: {
      magicLevel: 1,
      scienceLevel: 10,
      civilizationLevel: 10,
    },
  },
  {
    id: 'hollow-earth',
    name: '地球空洞説',
    description: '地球内部に存在する空洞世界',
    defaultParameters: {
      magicLevel: 6,
      scienceLevel: 4,
      dangerLevel: 7,
    },
  },
  {
    id: 'parallel-dimensions',
    name: '並行次元',
    description: '複数の次元が交差する世界',
    defaultParameters: {
      magicLevel: 9,
      scienceLevel: 8,
      dangerLevel: 8,
    },
  },
  {
    id: 'time-loop',
    name: '時間ループ',
    description: '時間が繰り返される世界',
    defaultParameters: {
      magicLevel: 5,
      scienceLevel: 6,
      dangerLevel: 6,
    },
  },
  {
    id: 'dream-world',
    name: '夢世界',
    description: '夢と現実の境界が曖昧な世界',
    defaultParameters: {
      magicLevel: 10,
      scienceLevel: 1,
      dangerLevel: 7,
    },
  },
  {
    id: 'digital-realm',
    name: 'デジタル世界',
    description: '電脳空間・仮想現実の世界',
    defaultParameters: {
      magicLevel: 1,
      scienceLevel: 10,
      civilizationLevel: 8,
    },
  },
  {
    id: 'pocket-dimension',
    name: 'ポケット次元',
    description: '小さな独立した次元空間',
    defaultParameters: {
      magicLevel: 8,
      scienceLevel: 4,
      dangerLevel: 5,
    },
  },
  {
    id: 'dying-world',
    name: '滅びゆく世界',
    description: '終末を迎えつつある世界',
    defaultParameters: {
      magicLevel: 4,
      scienceLevel: 5,
      dangerLevel: 9,
    },
  },
  {
    id: 'newborn-world',
    name: '生まれたての世界',
    description: '創造されたばかりの原初の世界',
    defaultParameters: {
      magicLevel: 10,
      scienceLevel: 1,
      civilizationLevel: 1,
    },
  },
  {
    id: 'shattered-world',
    name: '破砕された世界',
    description: '破壊され断片化した世界',
    defaultParameters: {
      magicLevel: 6,
      scienceLevel: 4,
      dangerLevel: 9,
    },
  },
  {
    id: 'inverted-world',
    name: '反転世界',
    description: '物理法則が逆転した世界',
    defaultParameters: {
      magicLevel: 7,
      scienceLevel: 3,
      dangerLevel: 8,
    },
  },
  {
    id: 'world-tree',
    name: '世界樹',
    description: '巨大な樹を中心とした世界',
    defaultParameters: {
      magicLevel: 9,
      scienceLevel: 2,
      civilizationLevel: 5,
    },
  },
  {
    id: 'cosmic-horror',
    name: '宇宙的恐怖',
    description: 'ラヴクラフト的な恐怖が支配する世界',
    defaultParameters: {
      magicLevel: 8,
      scienceLevel: 4,
      dangerLevel: 10,
    },
  },
  {
    id: 'clockwork-world',
    name: '歯車仕掛けの世界',
    description: '機械仕掛けで動く世界',
    defaultParameters: {
      magicLevel: 2,
      scienceLevel: 7,
      civilizationLevel: 6,
    },
  },
]

// カテゴリ別テンプレート
export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    id: 'genre',
    name: 'ジャンル特化',
    description: '特定のジャンルに最適化された世界観設定',
    templates: GENRE_TEMPLATES,
  },
  {
    id: 'special-structure',
    name: '特殊な世界構造',
    description: 'ユニークな物理構造や空間構造を持つ世界',
    templates: SPECIAL_STRUCTURE_TEMPLATES,
  },
]

// 後方互換性のため、すべてのテンプレートをフラットに結合
export const WORLD_TEMPLATES: WorldTemplateData[] = [
  ...GENRE_TEMPLATES,
  ...SPECIAL_STRUCTURE_TEMPLATES,
]